/* eslint-disable max-lines */
import React, { Suspense, lazy, useCallback, FC, HTMLAttributes, useRef, useState, useEffect } from 'react';
import { PdpLegalDisclaimer } from 'features/legal-disclaimer';
import { PdpBreadcrumps } from 'features/pdp-breadcrumps';
import { PdpNavigation } from 'features/page-navigation';
import { Sections } from 'features/page-navigation/components/section-refs-context';
import { Characteristics } from 'features/product-info/components/characteristics';
import { PdpD9nSection } from 'features/product-description/components';
import { PdpBottomD9nSectionState } from 'features/product-description/components/pdp-bottom-d9n-section';
import {
  PdpCollectionGoods,
  PdpComplementGoods,
  PdpComplementGoodsWithReco,
  PdpSuggestionsGoods,
} from 'features/related-products';
import { useRecoblockSelectors } from 'features/related-products/components/hooks/use-recoblock-selectors';
import { PdpPageWarningMessage } from 'features/page-warning-message';
import { Layout } from 'fronton-react';
import { useBreakpoints } from 'common/hooks';
import { ProductMediaContentConnected } from 'features/product-info/components/pdp-showcase/components/product-media-content';
import { ProductInfoContentConnected } from 'features/product-info/components/pdp-showcase/components';
import { Manuals } from 'features/manuals';
import { useWindowSize } from '@navi-toolbox/hooks';
import { pdpSectionStyle } from 'common/components/page-section';
import { cn } from 'utils';
import { cx } from 'linaria';
import { useRRPageView } from 'features/retail-rocket/hooks/use-rr-page-view';
import {
  productInformationContainer,
  productInfoDesktopPlusStyle,
  pageContent,
  paddingReset,
  descriptionContent,
  navigation,
  Containers,
  orderedContentNotSellable,
  Styles,
  sectionDivider,
} from './styles';
import { moveContent } from './utils/move-content';
import { PageContentMappedState } from './types';
import { useMonetizationTestVariant } from './utils/monetization-abcd';
import { useSelector } from 'react-redux';
import { getBreadcrumbs } from 'features/page-model';
import { getLinkedPagesLevel5 } from 'features/linked-pages/ducks/selectors';
import { LinkedPages } from 'features/linked-pages';
import { OtherProducts } from 'features/other-products';

const LazyReviews = lazy(() => import('./reviews-lazy'));
const LazySubstitutesProducts = lazy(() => import('./substitute-products-lazy'));
const LazySubstituteProductsWithReco = lazy(() => import('./substitute-products-with-reco-lazy'));

const descriptionRef = React.createRef<HTMLElement | null>();
const characteristicsRef = React.createRef<HTMLElement | null>();
const collectionGoods = React.createRef<HTMLElement | null>();
const reviewsRef = React.createRef<HTMLElement | null>();
const substituteGoods = React.createRef<HTMLElement | null>();
const complementGoods = React.createRef<HTMLElement | null>();

const refs: Sections.ContextType = new Map([
  ['description', descriptionRef],
  ['characteristics', characteristicsRef],
  ['collectionProducts', collectionGoods],
  ['complements', complementGoods],
  ['substitutes', substituteGoods],
  ['reviews', reviewsRef],
]);

export const PDP_COMPLEMENT_GOODS_ID = 'complements';

type PageContentProps = PageContentMappedState & HTMLAttributes<HTMLElement>;

export const PageContent: FC<PageContentProps> = ({
  seoProductDescription,
  productId,
  quantity,
  isAvailableForPurchase,
  regionId,
  ...htmlProps
}) => {
  const movementRef = useRef<HTMLDivElement>(null);
  const [complementBlockIsUp, setComplementBlockIsUp] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [newQuantity, setNewQuantity] = useState(0);
  const { width } = useWindowSize();
  const { mobile, smMobile } = useBreakpoints() || {};
  const onChangeCb = useCallback(
    (cancelAnimations = false) => {
      if (isAvailableForPurchase) {
        moveContent({
          movementRef,
          complementsRef: refs.get('complements'),
          newQuantity,
          setComplementBlockIsUp,
          isFirstTime: cancelAnimations ? false : isFirstTime,
          setIsFirstTime,
          cancelAnimations,
          mobile: mobile || smMobile,
        });
      }
    },
    [isAvailableForPurchase, isFirstTime, mobile, newQuantity, smMobile],
  );

  const { suggestionsPlacementId, complementsPlacementId, substitutesPlacementId } = useRecoblockSelectors();

  useEffect(() => {
    setNewQuantity(quantity);
  }, [quantity]);
  useEffect(() => {
    setIsFirstTime(true);
  }, []);
  useEffect(() => {
    onChangeCb();
  }, [
    newQuantity,
    complementBlockIsUp,
    isFirstTime,
    isAvailableForPurchase,
    mobile,
    smMobile,
    width,
    onChangeCb,
  ]);
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: true,
    largeDesktop: true,
  };
  const shouldDivideProductInfoContent = desktop || largeDesktop;

  useRRPageView(productId, regionId);

  const { showComplementsPlacement, showSubstitutesPlacement, showSuggestionsPlacement } =
    useMonetizationTestVariant();

  const breadcrumbs = useSelector(getBreadcrumbs) || [];
  const linkedPages = useSelector(getLinkedPagesLevel5);

  return (
    <>
      <Layout>
        <PdpBreadcrumps className={paddingReset} data={breadcrumbs} />
        <PdpPageWarningMessage />
        <div className={productInformationContainer}>
          <div className={pageContent} data-qa="content-container" {...htmlProps}>
            <ProductMediaContentConnected />
            {!shouldDivideProductInfoContent && <ProductInfoContentConnected />}
            <Sections.Context.Provider value={refs}>
              <PdpNavigation className={navigation} />
              <div className={isAvailableForPurchase ? descriptionContent : orderedContentNotSellable}>
                <Containers.ComplementMovementBlocks id="description-container" ref={movementRef}>
                  <PdpD9nSection
                    onCollapsedToggle={() => onChangeCb(true)}
                    className={cx(
                      Styles.pdpD9nSectionContainer,
                      complementBlockIsUp ? sectionDivider : undefined,
                    )}
                  />
                  <Characteristics
                    className={cx(
                      Styles.pdpSectionInsideInfoBlockStyle,
                      refs.get('description')?.current || complementBlockIsUp ? sectionDivider : undefined,
                    )}
                    onCollapsedToggle={() => onChangeCb(true)}
                  />

                  <PdpLegalDisclaimer className={Styles.pdpSectionInsideInfoBlockStyle} />
                  <Manuals />

                  <OtherProducts className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)} />

                  {isAvailableForPurchase && (
                    <PdpCollectionGoods
                      className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)}
                      id="collectionProducts"
                    />
                  )}
                  {showSuggestionsPlacement && suggestionsPlacementId && (
                    <section className={cn(Styles.pdpSectionInsideInfoBlockStyle, pdpSectionStyle)}>
                      <PdpSuggestionsGoods
                        placementId={suggestionsPlacementId}
                        productId={productId}
                        withDivider
                      />
                    </section>
                  )}
                  {!isAvailableForPurchase && (
                    <PdpCollectionGoods
                      className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)}
                      id="collectionProducts"
                    />
                  )}
                </Containers.ComplementMovementBlocks>
                <div>
                  {showComplementsPlacement && complementsPlacementId ? (
                    <PdpComplementGoodsWithReco
                      placementId={complementsPlacementId}
                      className={Styles.pdpSectionInsideInfoBlockStyle}
                      id={PDP_COMPLEMENT_GOODS_ID}
                      productId={productId}
                      withDivider={!complementBlockIsUp}
                    />
                  ) : (
                    <PdpComplementGoods
                      className={cx(
                        Styles.pdpSectionInsideInfoBlockStyle,
                        !complementBlockIsUp ? sectionDivider : undefined,
                      )}
                      id={PDP_COMPLEMENT_GOODS_ID}
                    />
                  )}
                  <Suspense fallback="...Загрузка">
                    {showSubstitutesPlacement && substitutesPlacementId ? (
                      <LazySubstituteProductsWithReco
                        placementId={substitutesPlacementId}
                        productId={productId}
                        withDivider
                      />
                    ) : (
                      <LazySubstitutesProducts className={sectionDivider} />
                    )}
                  </Suspense>
                </div>
                <Suspense fallback="...Загрузка">
                  <LazyReviews className={sectionDivider} />
                </Suspense>
              </div>
            </Sections.Context.Provider>
          </div>
          {shouldDivideProductInfoContent && (
            <div>
              <ProductInfoContentConnected className={productInfoDesktopPlusStyle} />
            </div>
          )}
        </div>

        {linkedPages && linkedPages.length > 0 && <LinkedPages pages={linkedPages} />}
      </Layout>
      <PdpBottomD9nSectionState />
    </>
  );
};
